import { defineStore } from "pinia";
import axios from "axios";
import { useAuthStore } from "@/stores/authStore";

export const useRegistrationStore = defineStore("registration", {
  state: () => ({
    formData: {
      username: "",       // Stores the user's chosen username
      email: "",          // Stores the user's email address
      password: "",       // Stores the user's password
      firstName: "",      // Stores the user's first name
      lastName: "",       // Stores the user's last name
      phoneNumber: "",    // Stores the user's phone number
    },
    status: {
      error: "",          // Holds error messages related to registration actions
      success: "",        // Holds success messages related to registration actions
    },
    isLoading: false,      // Indicates whether a registration-related request is in progress
    wpNonce: "",           // Stores the WordPress nonce for secure API requests
  }),

  actions: {
    /**
     * Resets the registration form fields to their default empty states.
     */
    resetFormData() {
      Object.keys(this.formData).forEach((key) => (this.formData[key] = ""));
    },

    /**
     * Handles user registration by sending form data to the server.
     * @returns {boolean} - Returns true if registration was successful, else false.
     */
    async register() {
      this.isLoading = true;
      this.status.error = "";
      this.status.success = "";

      const authStore = useAuthStore();

      try {
        const response = await axios.post(
          "my-plugin/v1/register",
          {
            username: this.formData.username,
            email: this.formData.email,
            password: this.formData.password,
            first_name: this.formData.firstName,
            last_name: this.formData.lastName,
            phone_number: this.formData.phoneNumber,
          },
          {
            headers: {
              "X-WP-Nonce": authStore.wpNonce, // Ensures the request is secure
            },
            withCredentials: true, // Includes cookies in the request
          }
        );

        if (response.data.success) {
          this.status.success = "Brugeren blev oprettet. Tjek venligst din email for at verificere din konto.";
          this.resetFormData(); // Clears the form upon successful registration
          return true;
        } else {
          this.status.error = response.data.message || "Registrering fejlede. Prøv venligst igen.";
          return false;
        }
      } catch (error) {
        this.handleAxiosError(error, "Registrering fejlede. Prøv venligst igen.");
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Handles Axios errors by setting appropriate error messages in the store.
     * @param {Object} error - The error object returned by Axios.
     * @param {string} defaultMessage - The default message to display if no specific message is available.
     */
    handleAxiosError(error, defaultMessage) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with a status other than 2xx
          this.status.error = error.response.data.message || defaultMessage;
        } else if (error.request) {
          // No response was received from the server
          this.status.error = "Ingen respons fra serveren. Kontroller din internetforbindelse.";
        } else {
          // Error occurred while setting up the request
          this.status.error = "Der opstod en fejl ved forberedelse af anmodningen.";
        }
      } else {
        // Non-Axios error occurred
        this.status.error = "Der opstod en uventet fejl.";
      }
    },
  },

  getters: {
    /**
     * Determines if the registration process has encountered any errors.
     * @param {Object} state - The store's state.
     * @returns {boolean} - True if there are errors, else false.
     */
    hasErrors: (state) => state.status.error.length > 0,

    /**
     * Determines if the registration process was successful.
     * @param {Object} state - The store's state.
     * @returns {boolean} - True if registration was successful, else false.
     */
    isSuccess: (state) => state.status.success.length > 0,
  },
});